/**
 * Notes tab — author-only edit/delete. Compose new notes via the rich-text
 * editor at the top (the same Slate editor the message center uses); existing
 * notes render below in reverse-chronological order.
 *
 * Notes carry an audience (Team / Admin only) and a type (Note / Call /
 * Meeting / Email / Task) chosen in the composer toolbar. New notes default to
 * Admin (private) for users who can choose it. The server filters reads to
 * match the audience: Admin only notes are visible to all client admins of the
 * account's client plus CBS internal users (including internal admins in the
 * internal portal) and hide from brokers; Team notes are visible to everyone
 * with account access. Brokers can only post to the Team.
 *
 * Typing `@` in the editor opens a typeahead scoped to the current user:
 * admins see the whole account team (admins + assigned broker + upline),
 * the assigned broker sees admins + their direct upline, and an upline broker
 * viewing a downline account sees admins + the assigned broker. Mentioned
 * users get a targeted in-app notification. For Admin only notes the universe
 * narrows to admins, since brokers couldn't open the note they were tagged in.
 */

import React from 'react';
import {Pencil, Trash2} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Card} from '@common-web/components/ui/card';
import {
  SlateEditor,
  SlateReadOnly,
  type MentionCandidate,
} from '@common-web/components/slate-editor';
import {
  EMPTY_SLATE_VALUE,
  isSlateValueEmpty,
  plainTextToSlate,
} from '@common-web/lib/slate-serialize';
import type {SlateValue} from '@common-web/lib/slate-types';
import {trpc, type RouterOutput} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';
import {
  NoteVisibilityPill,
  NoteVisibilitySelect,
  defaultNoteVisibility,
  type NoteVisibility,
} from '../components/NoteVisibilitySelect';
import {NoteTypePill, NoteTypeSelect, type NoteType} from '../components/NoteTypeSelect';

interface NotesTabProps {
  accountsId: string;
  /** Read-only virtual VBA account — notes can't be stored, hide the composer. */
  readOnly?: boolean;
}

type MentionableUser = RouterOutput['account']['listMentionableUsers'][number];

/**
 * Users offered by the composer's @ typeahead for a given audience. Admin only
 * notes hide from brokers, so brokers drop out of the universe; legacy
 * Internal notes are readable only by the author and CBS internal users, so no
 * one is offered — mirroring the server-side filter that gates mention
 * notifications.
 */
function mentionCandidatesFor(
  users: MentionableUser[] | undefined,
  visibility: NoteVisibility,
): MentionCandidate[] {
  if (!users || visibility === 'INTERNAL') return [];
  const visible = visibility === 'TEAM' ? users : users.filter((u) => u.role === 'CLIENT_ADMIN');
  return visible.map((user) => ({
    id: user.id,
    name: user.name,
    description: user.role === 'CLIENT_ADMIN' ? 'Client admin' : 'Broker',
  }));
}

/**
 * Parse a stored note body for editing. Bodies are normally
 * `JSON.stringify(Descendant[])`, but notes that predate the Slate migration are
 * plain text; those are wrapped into a Slate value (mirroring SlateReadOnly's
 * fallback) so legacy notes keep their content instead of opening blank.
 */
function parseEditableNoteBody(body: string | null | undefined): SlateValue {
  if (!body) return EMPTY_SLATE_VALUE;
  try {
    const parsed = JSON.parse(body);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed as SlateValue;
  } catch {
    // Not Slate JSON — treat as legacy plain text below.
  }
  return plainTextToSlate(body);
}

export function NotesTab({accountsId, readOnly = false}: NotesTabProps): React.ReactElement {
  const utils = trpc.useUtils();
  const currentUserId = useAuthStore((s) => s.user?.id);
  // Brokers may only direct notes to the Team; client admins and internal
  // users get the full audience picker.
  const canChooseRestricted = !useAuthStore((s) => s.isBrokerUser)();

  const [body, setBody] = React.useState<SlateValue>(EMPTY_SLATE_VALUE);
  const [visibility, setVisibility] = React.useState<NoteVisibility>(() =>
    defaultNoteVisibility(canChooseRestricted),
  );
  const [noteType, setNoteType] = React.useState<NoteType>('NOTE');
  // Bump to force-remount the Slate editor (which is uncontrolled after mount)
  // so it clears after a successful submit.
  const [composerKey, setComposerKey] = React.useState(0);

  const notes = trpc.account.getNotes.useQuery({accountsId});
  const mentionableUsers = trpc.account.listMentionableUsers.useQuery({accountsId});
  const addNote = trpc.account.addNote.useMutation({
    onSuccess: () => {
      setBody(EMPTY_SLATE_VALUE);
      setVisibility(defaultNoteVisibility(canChooseRestricted));
      setNoteType('NOTE');
      setComposerKey((k) => k + 1);
      utils.account.getNotes.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
    },
  });
  const deleteNote = trpc.account.deleteNote.useMutation({
    onSuccess: () => {
      utils.account.getNotes.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
    },
  });

  function handleAdd(): void {
    if (isSlateValueEmpty(body)) return;
    addNote.mutate({accountsId, body: JSON.stringify(body), visibility, type: noteType});
  }

  const isEmpty = isSlateValueEmpty(body);

  return (
    <Card className='space-y-4 border-border p-6 shadow-sm'>
      <h3 className='text-sm font-semibold uppercase tracking-wide text-foreground'>Notes</h3>

      {/* Composer */}
      {readOnly ? null : (
        <div className='space-y-2'>
          <SlateEditor
            key={composerKey}
            value={body}
            onChange={setBody}
            placeholder='Add a note · use @ to mention'
            minHeight={120}
            mentionUsers={mentionCandidatesFor(mentionableUsers.data, visibility)}
          />
          <div className='flex items-start justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <NoteTypeSelect
                value={noteType}
                onChange={setNoteType}
                disabled={addNote.isPending}
              />
            <NoteVisibilitySelect
              value={visibility}
              onChange={setVisibility}
              canChooseRestricted={canChooseRestricted}
              disabled={addNote.isPending}
            />
            </div>
            <div className='flex items-center gap-2'>
              {!isEmpty ? (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    setBody(EMPTY_SLATE_VALUE);
                    setVisibility(defaultNoteVisibility(canChooseRestricted));
                    setNoteType('NOTE');
                    setComposerKey((k) => k + 1);
                  }}
                >
                  Clear
                </Button>
              ) : null}
              <Button onClick={handleAdd} disabled={isEmpty || addNote.isPending}>
                {addNote.isPending ? 'Adding…' : 'Add note'}
              </Button>
            </div>
          </div>
          {addNote.error ? (
            <div className='text-sm text-red-600'>{addNote.error.message}</div>
          ) : null}
        </div>
      )}

      {/* Feed */}
      <div className='space-y-2'>
        {notes.isLoading ? (
          <div className='text-sm text-muted-foreground'>Loading notes…</div>
        ) : notes.data && notes.data.length > 0 ? (
          notes.data.map((note) => (
            <AccountNoteCard
              key={note.id}
              note={note}
              accountsId={accountsId}
              isAuthor={note.authorUserId === currentUserId}
              canChooseRestricted={canChooseRestricted}
              mentionableUsers={mentionableUsers.data}
              onDelete={() => deleteNote.mutate({noteId: note.id})}
              deletePending={deleteNote.isPending}
            />
          ))
        ) : (
          <div className='py-6 text-center text-sm text-muted-foreground'>No notes yet.</div>
        )}
      </div>
    </Card>
  );
}

type AccountNote = RouterOutput['account']['getNotes'][number];

interface AccountNoteCardProps {
  note: AccountNote;
  accountsId: string;
  isAuthor: boolean;
  canChooseRestricted: boolean;
  mentionableUsers: MentionableUser[] | undefined;
  onDelete: () => void;
  deletePending: boolean;
}

function AccountNoteCard({
  note,
  accountsId,
  isAuthor,
  canChooseRestricted,
  mentionableUsers,
  onDelete,
  deletePending,
}: AccountNoteCardProps): React.ReactElement {
  const utils = trpc.useUtils();
  const [editing, setEditing] = React.useState(false);
  const [editBody, setEditBody] = React.useState<SlateValue>(() =>
    parseEditableNoteBody(note.body),
  );
  const [editVisibility, setEditVisibility] = React.useState<NoteVisibility>(note.visibility);
  const [editType, setEditType] = React.useState<NoteType>(note.type);

  const updateNote = trpc.account.updateNote.useMutation({
    onSuccess: () => {
      setEditing(false);
      utils.account.getNotes.invalidate({accountsId});
      utils.account.getActivity.invalidate({accountsId});
    },
  });

  function startEdit(): void {
    setEditBody(parseEditableNoteBody(note.body));
    setEditVisibility(note.visibility);
    setEditType(note.type);
    setEditing(true);
  }

  function handleSave(): void {
    if (isSlateValueEmpty(editBody)) return;
    updateNote.mutate({
      noteId: note.id,
      body: JSON.stringify(editBody),
      visibility: editVisibility,
      type: editType,
    });
  }

  return (
    <div className='rounded-lg border border-border bg-muted/30 p-3'>
      {editing ? (
        <div className='space-y-2'>
          <SlateEditor
            value={editBody}
            onChange={setEditBody}
            minHeight={100}
            mentionUsers={mentionCandidatesFor(mentionableUsers, editVisibility)}
          />
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <NoteTypeSelect
                value={editType}
                onChange={setEditType}
                disabled={updateNote.isPending}
              />
              <NoteVisibilitySelect
                value={editVisibility}
                onChange={setEditVisibility}
                canChooseRestricted={canChooseRestricted}
                disabled={updateNote.isPending}
              />
            </div>
            <div className='flex items-center gap-2'>
              <Button variant='ghost' size='sm' onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button
                size='sm'
                onClick={handleSave}
                disabled={isSlateValueEmpty(editBody) || updateNote.isPending}
              >
                {updateNote.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
          {updateNote.error ? (
            <div className='text-sm text-red-600'>{updateNote.error.message}</div>
          ) : null}
        </div>
      ) : (
        <>
          <SlateReadOnly body={note.body} className='text-sm text-foreground' />
          <div className='mt-2 flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2 text-xs text-muted-foreground'>
              <span>
                {note.authorUser?.name ?? 'Unknown'} · {new Date(note.createdAt).toLocaleString()}
              </span>
              <NoteTypePill type={note.type} />
              <NoteVisibilityPill visibility={note.visibility} />
            </div>
            {isAuthor ? (
              <div className='flex items-center gap-1'>
                <Button size='sm' variant='ghost' onClick={startEdit} aria-label='Edit note'>
                  <Pencil className='h-3 w-3' />
                </Button>
                <Button
                  size='sm'
                  variant='ghost'
                  onClick={onDelete}
                  disabled={deletePending}
                  aria-label='Delete note'
                >
                  <Trash2 className='h-3 w-3' />
                </Button>
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
